"use client"

import { keepPreviousData, useQuery, type QueryKey, type UseQueryOptions } from "@tanstack/react-query"
import { toast } from "sonner"

type ApiQueryOptions<TData> = Omit<
    UseQueryOptions<TData, Error, TData, QueryKey>,
    "queryKey" | "queryFn"
> & {
    showErrorToast?: boolean
}

export function useApiQuery<TData>(
    queryKey: QueryKey,
    queryFn: () => Promise<TData>,
    options?: ApiQueryOptions<TData>
) {
    const { showErrorToast = true, ...queryOptions } = options ?? {}
    const placeholderData = queryOptions.placeholderData ?? keepPreviousData

    return useQuery({
        queryKey,
        queryFn: async () => {
            try {
                return await queryFn()
            } catch (error) {
                if (showErrorToast) {
                    const message = error instanceof Error ? error.message : "An error occurred"
                    toast.error(message)
                }
                throw error
            }
        },
        placeholderData,
        ...queryOptions,
    })
}
